import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { classInfoService } from '../services/ClassInfoService';
import './StudentClassesView.css';

function StudentClassesView({ student, schoolId }) {
  const [classInfo, setClassInfo] = useState(null);
  const [school, setSchool] = useState(null);
  const [learningMaterials, setLearningMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadClassData = useCallback(async () => {
    try {
      setLoading(true);
      setClassInfo(null);
      setSchool(null);
      setLearningMaterials([]);

      const classId = student.classId;
      if (!classId) {
        setLoading(false);
        return;
      }

      // Load class info
      const classData = await classInfoService.getClassInfo(classId);
      setClassInfo(classData);

      // Load school info using schoolId from props or classData
      const effectiveSchoolId = schoolId || classData.schoolId;
      if (effectiveSchoolId) {
        const schoolDoc = await getDoc(doc(db, 'schools', effectiveSchoolId));
        if (schoolDoc.exists()) {
          setSchool(schoolDoc.data());
        }
      }

      // Load learning materials
      const materialsRef = collection(db, 'classes', classId, 'learningMaterials');
      const snapshot = await getDocs(materialsRef);
      const materialsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by date (newest first)
      materialsData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setLearningMaterials(materialsData);
    } catch (error) {
      console.error('Error loading class data:', error);
    } finally {
      setLoading(false);
    }
  }, [student.classId, schoolId]);

  useEffect(() => {
    loadClassData();
  }, [loadClassData]);

  const getMaterialIcon = (type) => {
    const icons = {
      'PDF': '📄',
      'Document': '📝',
      'Image': '🖼️',
      'Video': '🎥',
      'Link': '🔗',
      'Presentation': '📊',
      'Other': '📎',
    };
    return icons[type] || '📎';
  };

  return (
    <div className="student-classes-view">
      <h2>My Classes</h2>

      {loading ? (
        <div className="loading">Loading class information...</div>
      ) : !classInfo ? (
        <div className="empty-state">
          <div className="empty-icon">🏫</div>
          <h3>No Class Assigned</h3>
          <p>Please contact your school administrator.</p>
        </div>
      ) : (
        <>
          <div className="class-card">
            <h3>{classInfo.name}</h3>
            <div className="class-details">
              {school && school.name && <p>🏫 School: {school.name}</p>}
              {classInfo.teacherName && <p>👨‍🏫 Teacher: {classInfo.teacherName}</p>}
              {classInfo.grade && <p>📚 Grade: {classInfo.grade}</p>}
              {classInfo.subject && <p>📖 Subject: {classInfo.subject}</p>}
              {classInfo.room && <p>📍 Room: {classInfo.room}</p>}
            </div>
          </div>

          <h3>Learning Materials</h3>
          {learningMaterials.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📚</div>
              <h3>No Learning Materials</h3>
              <p>Learning materials from your teacher will appear here.</p>
            </div>
          ) : (
            <div className="materials-list">
              {learningMaterials.map(material => (
                <div key={material.id} className="material-card">
                  <div className="material-header">
                    <span className="material-icon">{getMaterialIcon(material.materialType)}</span>
                    <h4>{material.title}</h4>
                  </div>
                  {material.description && <p className="description">{material.description}</p>}
                  <div className="material-meta">
                    {material.uploadedBy && <span>Uploaded by: {material.uploadedBy}</span>}
                    {material.createdAt && <span>{new Date(material.createdAt).toLocaleDateString()}</span>}
                  </div>
                  {material.fileURL && (
                    <a
                      href={material.fileURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="material-link"
                    >
                      Open File
                    </a>
                  )}
                  {material.externalLink && (
                    <a
                      href={material.externalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="material-link"
                    >
                      Open Link
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default StudentClassesView;
